import useGetProjectTasks from '@/api/queries/project/useGetProjectTasks';
import { dealTasksPath } from '@/ui/components/dealTasks/DealTasks';
import TaskCard from '@/ui/pages/projectPage/components/TaskCard';
import { AccordionWithChildCount } from '@payaca/ambrosia';

import { FC, useState } from 'react';
import { Link, useParams, useRouteMatch } from 'react-router-dom';

// Each link below carries the filters its count was taken with, so the Tasks
// tab shows the Tasks the count promised.
const OUTSTANDING_TASK_FILTERS = {
  completionStatuses: ['to do', 'in progress'],
  eventRelationshipStatuses: ['unlinked'],
};

const COMPLETE_TASK_FILTERS = {
  completionStatuses: ['complete'],
  eventRelationshipStatuses: ['unlinked'],
};

const TaskAccordion: FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { url } = useRouteMatch();
  // Todo: query to not get all the task information? as we only need the count...
  const { tasks: completeTasks } = useGetProjectTasks(
    projectId,
    COMPLETE_TASK_FILTERS
  );
  const { tasks: incompleteTasks } = useGetProjectTasks(projectId, {
    ...OUTSTANDING_TASK_FILTERS,
    sortBy: 'deadlineDate',
    sortDirection: 'asc',
  });

  const outstandingCount = incompleteTasks?.totalCount;
  const tasksBeyondPageLimit = incompleteTasks
    ? incompleteTasks.totalCount - incompleteTasks.items.length
    : undefined;

  // Undefined until the user toggles the group by hand — until then, `open`
  // tracks the outstanding count directly, opening the instant it arrives
  // rather than only at the next remount. Once set, the user's own choice
  // wins over the count from then on.
  const [isOpen, setIsOpen] = useState<boolean>();

  return (
    <AccordionWithChildCount
      count={`${outstandingCount ?? '-'}`}
      open={isOpen ?? !!outstandingCount}
      onOpenChange={setIsOpen}
      title="Project tasks"
      titleTooltip="Incomplete tasks not linked to events"
      chevronPosition="left"
    >
      {!incompleteTasks?.items.length && (
        <p className="mt-2">No project tasks to do.</p>
      )}
      {incompleteTasks?.items.map((task) => {
        return (
          <TaskCard key={task.id} to={`${url}/tasks/${task.id}`} {...task} />
        );
      })}
      {(tasksBeyondPageLimit ?? 0) > 0 && (
        <div>
          <Link to={dealTasksPath(projectId, OUTSTANDING_TASK_FILTERS)}>
            + {tasksBeyondPageLimit} more task
            {tasksBeyondPageLimit === 1 ? '' : 's'}
          </Link>
        </div>
      )}

      {completeTasks && completeTasks.totalCount > 0 && (
        <div className="mt-2">
          <Link to={dealTasksPath(projectId, COMPLETE_TASK_FILTERS)}>
            {completeTasks.totalCount} completed
          </Link>
        </div>
      )}
    </AccordionWithChildCount>
  );
};

export default TaskAccordion;
